<script setup>
import notification from "@/utils/plugins/NotificationService";
import { onMounted, ref, computed, watch } from "vue";
import { ArrowRightBold } from "@element-plus/icons-vue";

import { useUiStore } from "@/app/stores/uiStore";

import TrackerReportEntry from "@/components/shared/TrackerReportEntry.vue";
import TrackerTotalTime from "@/components/shared/TrackerTotalTime.vue";
import SharedDeleteConfirmDialog from "@/components/shared/SharedDeleteConfirmDialog.vue";

import TrackerService from "@/api/services/tracker.service";
import TrackerReportEdit from "./TrackeReportEdit.vue";
const uiStore = useUiStore();

const totalAmount = ref(0);
const selectedEntries = ref([]);
const dialogEdit = ref(false);
const discardDialogShown = ref(false);
const isAllSelected = ref(false);

const props = defineProps({
  entries: Array,
  totalHoursAll: Number,
  isActiveHoursAll: {
    type: Boolean,
    default: false,
  },
  groupByDay: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(["reloadEntries"]);

const reloadEntries = () => {
  emits("reloadEntries");
};

const totalHours = computed(() => {
  let duration = 0;
  if (entries.value.length > 0) {
    for (let i = 0; i < entries.value.length; i++) {
      const entrie = entries.value[i];
      duration += entrie.duration;
    }
  }

  return duration;
});

const totalHoursFacturable = computed(() => {
  let duration = 0;
  if (entries.value.length > 0) {
    for (let i = 0; i < entries.value.length; i++) {
      const entrie = entries.value[i];
      if (entrie.billable) {
        duration += entrie.duration;
      }
    }
  }

  return duration;
});

const totalHoursAll = computed(() => {
  if (props && props.totalHoursAll && props.totalHoursAll >= 0) {
    return props.totalHoursAll;
  } else {
    return 0;
  }
});

const isActiveTotalAll = computed(() => {
  if (props && props.isActiveHoursAll) {
    return props.isActiveHoursAll;
  } else {
    return false;
  }
});

const entries = computed(() => {
  return props.entries || [];
});

const groupByDay = computed(() => {
  return props.groupByDay;
});

const expandedDayGroups = ref([]);

watch(
  entries,
  (newEntries) => {
    selectedEntries.value = selectedEntries.value.filter((entry) =>
      newEntries.includes(entry)
    );
    isAllSelected.value =
      newEntries.length > 0 && selectedEntries.value.length === newEntries.length;
  },
  { immediate: true }
);

watch(groupByDay, (isGrouped) => {
  if (!isGrouped) {
    expandedDayGroups.value = [];
  }
});

onMounted(() => {
  totalAmount.value = 0;
});

// Funcionalidad checkbox
const updateSelection = (entry, isChecked) => {
  if (isChecked) {
    if (!selectedEntries.value.includes(entry)) {
      selectedEntries.value.push(entry);
    }
  } else {
    selectedEntries.value = selectedEntries.value.filter((item) => item !== entry);
  }
  isAllSelected.value = selectedEntries.value.length === entries.value.length;
};

const formatDuration = (duration) => {
  let hours = 0;
  let minutes = 0;
  let remainingSeconds = duration;
  do {
    if (remainingSeconds >= 60) {
      remainingSeconds = remainingSeconds - 60;
      minutes = minutes + 1;
    }
    if (minutes >= 60) {
      minutes = minutes - 60;
      hours = hours + 1;
    }
  } while (remainingSeconds >= 60);
  const formattedHours = hours.toLocaleString("es-ES", { useGrouping: true });
  return `${formattedHours}:${minutes
    .toString()
    .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const selectAllEntries = (newVal) => {
  isAllSelected.value = newVal;
  if (!isAllSelected.value) {
    selectedEntries.value = [];
  } else {
    selectedEntries.value = [...entries.value];
  }
};

const isSomeEntriesSelected = computed(() => {
  return (
    selectedEntries.value.length > 0 &&
    selectedEntries.value.length < entries.value.length
  );
});

// Funcionalidad eliminar
const deleteEntries = () => {
  if (selectedEntries.value.length < 1) {
    notification.addNotification({
      message: "Tienes que elegir alguna entrada",
      type: "warning",
      duration: 6000,
    });
    return;
  }

  discardDialogShown.value = true;
};

const confirmDeleteEntries = async () => {
  try {
    uiStore.startLoading();
    for (let i = 0; i < selectedEntries.value.length; i++) {
      const id = selectedEntries.value[i].id;
      await TrackerService.discardEntry(id);
    }
    uiStore.stopLoading();
    reloadEntries();
    let message =
      selectedEntries.value.length > 1 ? "Las entradas se han" : "La entrada se ha";
    notification.addNotification({
      message: message + " eliminado",
      type: "success",
      duration: 6000,
    });
    selectedEntries.value = [];
    discardDialogShown.value = false;
  } catch {
    uiStore.stopLoading();
  }
};

// Funcionalidad Dialog Edit

const closeDialogEdit = () => {
  dialogEdit.value = false;
};

const openDialogEdit = () => {
  if (selectedEntries.value.length < 1) {
    notification.addNotification({
      message: "Tienes que elegir alguna entrada",
      type: "warning",
      duration: 6000,
    });
    return;
  }
  dialogEdit.value = true;
};

const getDayKey = (entry) => {
  return entry?.timeStart ? entry.timeStart.split("T")[0] : "without-date";
};

const capitalizeTitle = (text) => {
  if (!text) {
    return "";
  }

  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatGroupDate = (dateKey) => {
  if (!dateKey || dateKey === "without-date") {
    return "Sin fecha";
  }

  const localDate = new Date(`${dateKey}T00:00:00`);
  const formattedDate = new Intl.DateTimeFormat("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(localDate);

  return capitalizeTitle(formattedDate);
};

const toggleDayGroup = (dayKey) => {
  if (expandedDayGroups.value.includes(dayKey)) {
    expandedDayGroups.value = expandedDayGroups.value.filter((key) => key !== dayKey);
    return;
  }

  expandedDayGroups.value.push(dayKey);
};

const isDayGroupExpanded = (dayKey) => {
  return expandedDayGroups.value.includes(dayKey);
};

const groupedEntriesByDay = computed(() => {
  if (!groupByDay.value || entries.value.length === 0) {
    return [];
  }

  const groupedEntries = new Map();

  entries.value.forEach((entry) => {
    const dayKey = getDayKey(entry);
    if (!groupedEntries.has(dayKey)) {
      groupedEntries.set(dayKey, []);
    }

    groupedEntries.get(dayKey).push(entry);
  });

  return Array.from(groupedEntries.entries()).map(([dayKey, dayEntries]) => {
    const totalDuration = dayEntries.reduce(
      (total, entry) => total + (entry.duration || 0),
      0
    );

    return {
      dayKey,
      label: formatGroupDate(dayKey),
      totalDuration,
      entries: dayEntries,
    };
  });
});

const groupedDayKeys = computed(() => {
  return groupedEntriesByDay.value.map((group) => group.dayKey);
});

const isAllDayGroupsExpanded = computed(() => {
  return (
    groupedDayKeys.value.length > 0 &&
    expandedDayGroups.value.length === groupedDayKeys.value.length
  );
});

const isSomeDayGroupExpanded = computed(() => {
  return (
    expandedDayGroups.value.length > 0 &&
    expandedDayGroups.value.length < groupedDayKeys.value.length
  );
});

const collapseAllLabel = computed(() => {
  return isAllDayGroupsExpanded.value ? "Colapsar" : "Descolapsar";
});

const toggleAllDayGroups = () => {
  if (isAllDayGroupsExpanded.value) {
    expandedDayGroups.value = [];
    return;
  }

  expandedDayGroups.value = [...groupedDayKeys.value];
};

watch(
  groupedEntriesByDay,
  (newGroups) => {
    const validKeys = newGroups.map((group) => group.dayKey);
    expandedDayGroups.value = expandedDayGroups.value.filter((dayKey) =>
      validKeys.includes(dayKey)
    );
  },
  { immediate: true }
);
</script>
<template>
  <el-card class="card-tracker mt-3">
    <template #header>
      <div class="card-tracker-header">
        <div class="row ms-0 card-tracker-totals" style="min-width: 950px">
          <div class="col-3 ms-0 el-col-xs-24" v-if="isActiveTotalAll">
            Total:
            <span class="inputHour inputHour--strong">
              {{ formatDuration(totalHoursAll) }}
            </span>
          </div>
          <div class="col-3 ms-0 el-col-xs-24">
            Total (página):
            <span class="inputHour">
              {{ formatDuration(totalHours) }}
            </span>
          </div>
          <div class="col-3 ms-0 el-col-xs-12">
            Facturable:
            <span class="inputHour inputNotTotal">
              {{ formatDuration(totalHoursFacturable) }}
            </span>
          </div>
          <div class="col-3 ms-0 el-col-xs-12">
            Importe:
            <span class="inputHour inputNotTotal">
              {{ formatDuration(totalAmount) }}
            </span>
          </div>
        </div>
      </div>
    </template>
    <template #default>
      <div class="col-12">
        <div class="row d-flex mb-2 mt-1">
          <div class="col-5 columnReport1 el-col-xs-24">
            <div class="row d-flex">
              <div class="col-auto">
                <span class="editSpan" @click="openDialogEdit">Edicion en masa</span>
              </div>
              <div class="col-auto">
                <span class="deleteFilter" @click="deleteEntries">Eliminar</span>
              </div>
              <div class="col-auto" style="margin-top: -6px">
                <el-checkbox v-model="isAllSelected" :indeterminate="isSomeEntriesSelected"
                  @change="selectAllEntries" />
                <span class="editSpan" @click="selectAllEntries(!isAllSelected)" style="margin-left: 5px">{{
                  isAllSelected ? "Quitar selección" : "Seleccionar todos" }}</span>
              </div>
              <div v-if="groupByDay && groupedEntriesByDay.length > 0" class="col-auto collapse-all-control"
                style="margin-top: -6px">
                <el-checkbox :model-value="isAllDayGroupsExpanded" :indeterminate="isSomeDayGroupExpanded"
                  @change="toggleAllDayGroups" />
                <span class="editSpan" @click="toggleAllDayGroups" style="margin-left: 5px">
                  {{ collapseAllLabel }}
                </span>
              </div>
            </div>
          </div>
          <div class="col-7 columnReport1 el-col-xs-24 secondColumnReport">
            <div class="row d-flex" style="margin-left: 10px">
              <div class="col-3">
                <span>Importe</span>
              </div>
              <div class="col-2">
                <span>Usuario</span>
              </div>
              <div class="col-3" style="margin-left: 15px">
                <span>Tiempo</span>
              </div>
              <div class="col-3" style="margin-left: 15px">
                <span>Duracion</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12">
        <hr v-if="!groupByDay" style="margin-bottom: 5px" />
        <el-checkbox-group v-model="selectedEntries">
          <template v-if="groupByDay">
            <div v-for="group in groupedEntriesByDay" :key="group.dayKey" class="day-group">
              <button type="button" class="card-tracker-header day-group__header"
                :class="{ 'day-group__header--expanded': isDayGroupExpanded(group.dayKey) }"
                @click="toggleDayGroup(group.dayKey)">
                <span class="day-group__title-content">
                  <el-icon class="day-group__icon"
                    :class="{ 'day-group__icon--expanded': isDayGroupExpanded(group.dayKey) }">
                    <ArrowRightBold />
                  </el-icon>
                  <span class="day-group__title">{{ group.label }}</span>
                </span>
                <TrackerTotalTime :totalTime="group.totalDuration" />
              </button>
              <el-collapse-transition>
                <div v-show="isDayGroupExpanded(group.dayKey)">
                  <TrackerReportEntry v-for="(entry, entryIndex) in group.entries" :key="entry.id" :entry="entry"
                    :selected-entries="selectedEntries" @update-selection="updateSelection"
                    @reload-entries="reloadEntries" :show-divider="entryIndex !== group.entries.length - 1" />
                </div>
              </el-collapse-transition>
            </div>
          </template>
          <template v-else>
            <TrackerReportEntry v-for="entry in entries" :key="entry.id" :entry="entry"
              :selected-entries="selectedEntries" @update-selection="updateSelection" @reload-entries="reloadEntries" />
          </template>
        </el-checkbox-group>
      </div>
    </template>
  </el-card>
  <div>
    <el-dialog v-model="dialogEdit" title="Edicion Masiva de una entrada" width="650" class="trackerReportEdit"
      style="height: 630px; overflow: hidden; border-radius: 10px" :lock-scroll="true" v-if="selectedEntries.length > 0"
      @close="closeDialogEdit">
      <template #header>
        <div class="custom-header">Edición masiva de una entrada</div>
      </template>
      <TrackerReportEdit v-if="dialogEdit" :selectedEntries="selectedEntries" @reload-entries="reloadEntries"
        @close-dialog="closeDialogEdit" />
    </el-dialog>
  </div>
  <SharedDeleteConfirmDialog v-model="discardDialogShown" title="Eliminar entrada"
    :message="'¿Estás seguro de descartar ' + (selectedEntries.length > 1 ? 'estas entradas' : 'esta entrada') + ' ?'"
    @confirm="confirmDeleteEntries" />
</template>
<style lang="scss" scoped>
.inputHour {
  font-weight: 500;
  font-size: 24px;
  color: #000;
}

.inputHour--strong {
  font-weight: 600;
}

.editSpan {
  text-transform: uppercase;
  font-size: 16px;
  color: #474ce6;
  cursor: pointer;
}

.collapse-all-control {
  display: inline-flex;
  align-items: center;
}

:deep(.el-dialog__header) {
  padding: 0;
  margin-right: -33px;
}

:deep(.el-dialog__headerbtn .el-dialog__close) {
  color: white !important;
}

:deep(.el-dialog__headerbtn .el-dialog__close:hover) {
  color: #f0f0f0 !important;
}

.custom-header {
  background-color: $bg-blue;
  text-transform: uppercase;
  color: white;
  text-align: left;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  width: 100%;
  margin-bottom: 10px;
  margin-top: -16px;
  margin-left: -17px;

  span {
    margin-left: 5px;
  }
}

.day-group {
  margin-bottom: 18px;
}

.day-group__header {
  border-bottom-left-radius: 15px !important;
  border-bottom-right-radius: 15px !important;
  border: none;
  cursor: pointer;
  text-align: left;
  margin-bottom: 10px;
  min-height: 48px;
  transition: border-radius 0.2s ease;
  width: 100%;
}

.day-group__header--expanded {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  margin-bottom: 0;
}

.day-group__title-content {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 10px;
  min-width: 0;
}

.day-group__icon {
  flex-shrink: 0;
  font-size: 12px;
  transition: transform 0.2s ease;
}

.day-group__icon--expanded {
  transform: rotate(90deg);
}

.day-group__title {
  display: block;
  font-weight: 600;
  line-height: 1.25;
  padding-right: 12px;
  white-space: normal;
  word-break: break-word;
}

.day-group__header:focus-visible {
  outline: 2px solid #474ce6;
  outline-offset: 2px;
}

@media (max-width: 767px) {
  .card-tracker-totals {
    min-width: 100px !important;

    .inputNotTotal {
      font-size: 16px;
    }
  }

  .card-tracker-header {
    padding: 5px 12px !important;
  }

  .secondColumnReport {
    display: none;
  }

  .day-group__header {
    align-items: center;
    flex-direction: row;
    gap: 12px;
  }

  .day-group__title {
    min-width: 0;
  }

  :deep(.day-group__header .tracker-total-time) {
    align-items: center;
    flex-direction: row;
    margin-left: auto;
    white-space: nowrap;
  }

  :deep(.day-group__header .tracker-total-time .tracker-total-time__time) {
    font-size: 24px !important;
    font-weight: 500;
    margin-left: 5px;
    min-width: 100px;
    text-align: right;
  }
}

@media (max-width: 520px) {
  .day-group__header {
    gap: 8px;
  }

  .day-group__title-content {
    gap: 8px;
  }

  .day-group__title {
    font-size: 13px;
    padding-right: 8px;
  }

  :deep(.day-group__header .tracker-total-time .tracker-total-time__title span) {
    font-size: 13px;
  }

  :deep(.day-group__header .tracker-total-time .tracker-total-time__time) {
    font-size: 18px !important;
    min-width: 78px;
  }
}
</style>
